/**
 * Normalized Scoring Integration for scoreEngine
 * 
 * This module wraps the existing scoreCombination function to support
 * both legacy scoring and new normalized scoring with A/B testing capability
 */

import { MATCHMAKING_CONFIG, debugLog } from './matchmakingConfig.js';
import { getSessionDoublesGameCount } from './gameTypeBudget.js';
import {
  computeComboVarietyDebt,
  computeSessionDebtFairnessBoost,
  computeRecentLoadDebt,
  computeQuartetNovelty,
  getSessionDebtConfig,
  isSessionDebtModelEnabled,
} from './sessionDebt.js';
import {
  isScoringFirstMatchmakingEnabled,
  applyScoringFirstComboAdjustments,
} from './scoringFirstMatchmaking.js';
import { scorePlayerPreferences, checkHardMatchPreferenceBlock } from './scoreEngine.js';
import { 
  calculateFairnessScore,
  calculateGamesAboveMinPenalty,
  calculateImmediateReselectPenalty,
  calculateEloBalanceScore,
  calculateInteractionScore,
  calculateGameTypeScore,
  calculateCategoryScore,
  calculateFinalScore,
  checkExactRepeatGame,
  checkPartnershipViolation,
  checkMinPlus2FairnessGuard,
  checkImmediateRepeatTeam,
  checkRecentRepeatTeam,
  checkImmediateSinglesRematch,
  checkRecentSinglesRematch,
  checkCooldownHardBlock,
  DEFAULT_SCORING_CONFIG,
  getSessionGameTypeCounts,
  getProposedGameType,
} from './normalizedScoring.js';

/**
 * Compute an additive final-score boost for the proposed game type when it is
 * under-represented in the session history.  Scaled by how many games behind the
 * second-lowest type, capped by config.gameTypeBalance.deficitBoostCap.
 *
 * Returns 0 when the feature is disabled, no session history exists, or the
 * proposed type is not the current minimum.
 */
function computeGameTypeDeficitBoost(team1Players, team2Players, sessionState, playersById) {
  const balanceConfig = MATCHMAKING_CONFIG.gameTypeBalance;
  if (!balanceConfig?.enabled || !sessionState || !playersById) return 0;

  const minGamesForBoost = balanceConfig.minSessionDoublesGamesForDeficitBoost ?? 6;
  if (getSessionDoublesGameCount(sessionState, playersById) < minGamesForBoost) {
    return 0;
  }

  const proposedType = getProposedGameType(
    team1Players,
    team2Players,
    sessionState?.playerWaitMetrics || {},
    playersById,
  );
  if (!proposedType) return 0;

  // Women's doubles requires at least womensMinFemaleCount females in the proposed match
  if (proposedType === 'womens') {
    const allPlayers = [...team1Players, ...team2Players];
    const femaleCount = allPlayers.filter(p => (p?.gender || '').toLowerCase() === 'female').length;
    if (femaleCount < (balanceConfig.womensMinFemaleCount ?? 4)) return 0;
  }

  const counts = getSessionGameTypeCounts(sessionState, playersById, sessionState?.playerWaitMetrics);
  const countForType = counts[proposedType];
  const sorted = [counts.mixed, counts.mens, counts.womens].sort((a, b) => a - b);
  const minCount = sorted[0];
  const secondLowest = sorted[1];

  if (countForType !== minCount) return 0; // Not the current minimum type

  const gamesBehind = Math.max(0, secondLowest - countForType);
  const perGame = balanceConfig.deficitBoostPerGameBehind ?? 0.8;
  const cap = balanceConfig.deficitBoostCap ?? 15;
  return Math.min(cap, perGame * gamesBehind);
}

/**
 * Score a combination using the normalized scoring system
 * @param {Object} split - Team split {team1: [], team2: []}
 * @param {Object} playersById - Map of player objects
 * @param {Array} partnerships - Partnership array
 * @param {Object} sessionState - Session state
 * @param {number} relaxationLevel - Relaxation level
 * @param {Array} scheduledCombinations - Scheduled game combinations
 * @param {string} gameTypeRestriction - Game type restriction
 * @param {Object} playerCategories - Player categories
 * @param {number} gamesMin - Minimum games
 * @param {Array} scheduledGames - Scheduled games
 * @param {Set} playersPassedCooldown - Players passed cooldown
 * @param {number} expandedThreshold - Expanded threshold
 * @param {Object} scoringConfig - Optional scoring configuration
 * @param {Object} hardBlocks - Optional hard block overrides from relaxation system
 * @param {boolean} cooldownEnabled - Session-level cooldown setting
 * @returns {number} Final score (0-100 scale) or -Infinity for hard blocks
 */
export function scoreCombinationNormalized(
  split,
  playersById,
  partnerships,
  sessionState,
  relaxationLevel,
  scheduledCombinations,
  gameTypeRestriction,
  playerCategories,
  gamesMin,
  scheduledGames,
  playersPassedCooldown,
  expandedThreshold,
  scoringConfig = null,
  hardBlocks = null,
  cooldownEnabled = false,
  poolPlayerIds = null,
) {
  const { team1, team2 } = split;
  const team1Objects = team1.map(id => playersById[id]);
  const team2Objects = team2.map(id => playersById[id]);
  const isSinglesProposed = team1.length === 1 && team2.length === 1;

  if (team1Objects.some((p) => !p) || team2Objects.some((p) => !p)) {
    return {
      score: -Infinity,
      blockReason: 'Missing player data',
      dimensionScores: null,
    };
  }
  
  // Use provided config or default
  const config = scoringConfig || DEFAULT_SCORING_CONFIG;
  
  // ========================================
  // HARD BLOCK CHECKS (Return -Infinity immediately if violated)
  // ========================================
  
  // 1. Partnership Violations (NEVER RELAXED)
  const partnershipCheck = checkPartnershipViolation(team1, team2, partnerships, { poolPlayerIds });
  if (partnershipCheck === -Infinity) {
    return {
      score: -Infinity,
      blockReason: 'Partnership violation - partners must be on same team',
      dimensionScores: null,
    };
  }

  // 1b. Hard match preferences (NEVER RELAXED)
  if (sessionState?.matchPreferencesEnabled !== false) {
    const hardPrefCheck = checkHardMatchPreferenceBlock(
      team1,
      team2,
      playersById,
      sessionState?.playerWaitMetrics || {},
    );
    if (hardPrefCheck === -Infinity) {
      return {
        score: -Infinity,
        blockReason: 'Hard match preference violation',
        dimensionScores: null,
      };
    }
  }
  
  // 2. Min+2 Fairness Guard (relaxable at level 5)
  const minPlus2Check = checkMinPlus2FairnessGuard(team1, team2, playersById, gamesMin, hardBlocks, {
    poolPlayerIds,
    sessionWideMinGames: sessionState?.sessionWideMinGames ?? null,
    sessionState,
    playerWaitMetrics: sessionState?.playerWaitMetrics || {},
  });
  if (minPlus2Check === -Infinity) {
    return {
      score: -Infinity,
      blockReason: 'Min+2 fairness guard - player would reach min+3 unfairly',
      dimensionScores: null,
    };
  }
  
  // 3. Immediate repeat (doubles: same pair same team; singles: immediate 1v1 rematch)
  const immediateRepeatCheck = isSinglesProposed
    ? checkImmediateSinglesRematch(team1, team2, sessionState, hardBlocks, scheduledCombinations)
    : checkImmediateRepeatTeam(
      team1, 
      team2, 
      sessionState, 
      partnerships, 
      playersById, 
      hardBlocks,
      scheduledCombinations
    );
  if (immediateRepeatCheck === -Infinity) {
    return {
      score: -Infinity,
      blockReason: isSinglesProposed
        ? 'Immediate singles rematch - same 1v1 just played'
        : 'Immediate repeat team - same pair just played together',
      dimensionScores: null,
    };
  }

  // 3b. Recent repeat (doubles: same partnership; singles: same 1v1 within window)
  const recentRepeatCheck = isSinglesProposed
    ? checkRecentSinglesRematch(team1, team2, sessionState, hardBlocks, scheduledCombinations)
    : checkRecentRepeatTeam(
      team1,
      team2,
      sessionState,
      partnerships,
      playersById,
      hardBlocks,
      scheduledCombinations
    );
  if (recentRepeatCheck === -Infinity) {
    return {
      score: -Infinity,
      blockReason: isSinglesProposed
        ? 'Recent singles rematch - same 1v1 played within window'
        : 'Recent repeat team - same pair played together recently',
      dimensionScores: null,
    };
  }

  // 4. Cooldown Hard Block (progressive relaxation tier)
  const cooldownCheck = checkCooldownHardBlock(
    team1, 
    team2, 
    playersPassedCooldown, 
    hardBlocks, 
    cooldownEnabled
  );
  if (cooldownCheck === -Infinity) {
    return {
      score: -Infinity,
      blockReason: 'Cooldown violation - player has not passed cooldown',
      dimensionScores: null,
    };
  }
  
  // ========================================
  // DIMENSION SCORING (0-100 or -Infinity for violations)
  // ========================================
  
  // Determine game type for ELO balance scoring
  const team1Mixed = team1Objects.some(p => p?.gender === 'male') &&
                     team1Objects.some(p => p?.gender === 'female');
  const team2Mixed = team2Objects.some(p => p?.gender === 'male') &&
                     team2Objects.some(p => p?.gender === 'female');
  const isMixed = team1Mixed && team2Mixed;
  
  let gameTypeForElo = 'levels'; // Default
  if (isMixed) {
    gameTypeForElo = 'mixed';
  } else if (gameTypeRestriction === 'all_types') {
    gameTypeForElo = 'all';
  }
  
  // Calculate dimension scores (passing hardBlocks to checks that need it)
  const allPlayerObjects = [...team1Objects, ...team2Objects];
  let fairnessScore = calculateFairnessScore(allPlayerObjects, gamesMin);

  if (isSessionDebtModelEnabled() && !isScoringFirstMatchmakingEnabled()) {
    const debtBoost = computeSessionDebtFairnessBoost(
      team1Objects,
      team2Objects,
      sessionState,
      playersById,
      gamesMin,
    );
    fairnessScore = Math.min(100, Math.max(0, fairnessScore + debtBoost));
  }

  const dimensionScores = {
    fairness: fairnessScore,
    
    eloBalance: calculateEloBalanceScore(
      team1Objects,
      team2Objects,
      gameTypeForElo,
      sessionState?.playerWaitMetrics || {}
    ),
    
    interactions: calculateInteractionScore(
      team1,
      team2,
      sessionState,
      partnerships,
      hardBlocks,
      scheduledCombinations,
    ),
    
    gameType: calculateGameTypeScore(
      team1Objects,
      team2Objects,
      gameTypeRestriction,
      hardBlocks,  // Pass hardBlocks for game type enforcement
      sessionState,  // For under-represented game type bonus (mens/mixed/womens)
      playersById,
      sessionState?.playerWaitMetrics || {}
    ),
    
    categories: calculateCategoryScore(
      team1Objects,
      team2Objects,
      playerCategories,
      hardBlocks,  // Pass hardBlocks for category gap check
      sessionState?.playerWaitMetrics || {}
    ),
    
    // Same-quartet / identical-team variety is soft-scored in interactions (never hard-blocked)
    repeatGame: checkExactRepeatGame(team1, team2, sessionState, null, scheduledCombinations)
      ? 25
      : 100,
  };
  
  // Calculate final weighted score
  let finalScore = calculateFinalScore(dimensionScores, config);

  if (finalScore !== -Infinity && isScoringFirstMatchmakingEnabled()) {
    finalScore = applyScoringFirstComboAdjustments(
      finalScore,
      team1,
      team2,
      team1Objects,
      team2Objects,
      sessionState,
      playersById,
      gamesMin,
      partnerships,
    );
  } else if (finalScore !== -Infinity && isSessionDebtModelEnabled()) {
    const varietyDebt = computeComboVarietyDebt(
      team1Objects,
      team2Objects,
      sessionState,
      partnerships,
    );
    finalScore += (varietyDebt - 50) * 0.2;

    const comboIds = [...team1, ...team2];
    const quartetNovelty = computeQuartetNovelty(comboIds, sessionState);
    const saturation = sessionState?.varietySaturation;
    const saturated =
      (saturation?.repeatQuartetRate ?? 0) >= 0.2
      || (saturation?.score ?? 0) >= (MATCHMAKING_CONFIG.variety?.watchThreshold ?? 0.25);
    if (quartetNovelty === 0) {
      finalScore -= saturated ? 45 : 15;
      if (saturated) {
        debugLog('🔁 Saturated session: penalising exact repeat quartet', saturation);
      }
    } else if (saturated) {
      finalScore += 30;
      debugLog('✨ Saturated session: boosting novel quartet', saturation);
    }
  }

  // Penalise putting a just-finished player onto the post-score upcoming (additive fairness).
  if (finalScore !== -Infinity && sessionState) {
    const restPenalty = calculateImmediateReselectPenalty(
      [...team1, ...team2],
      sessionState,
    );
    if (restPenalty > 0) {
      debugLog('🪑 Immediate reselect penalty applied:', {
        restPenalty,
        finalScoreBefore: finalScore,
      });
      finalScore -= restPenalty;
    }
  }

  // Penalise each game above session minimum (additive, outside weighted dimensions).
  if (finalScore !== -Infinity && gamesMin !== null && gamesMin !== undefined) {
    const aboveMinPenalty = calculateGamesAboveMinPenalty(
      [...team1Objects, ...team2Objects],
      gamesMin,
    );
    if (aboveMinPenalty > 0) {
      debugLog('⚖️ Games-above-min penalty applied:', {
        gamesMin,
        aboveMinPenalty,
        finalScoreBefore: finalScore,
      });
      finalScore -= aboveMinPenalty;
    }

    if (isSessionDebtModelEnabled() && !isScoringFirstMatchmakingEnabled()) {
      const config = getSessionDebtConfig();
      let recentLoadPenalty = 0;
      for (const player of allPlayerObjects) {
        recentLoadPenalty += computeRecentLoadDebt(
          player.id,
          sessionState,
          config.recentWindowGames,
        ) * config.weights.recent * 10;
      }
      if (recentLoadPenalty > 0) {
        finalScore -= recentLoadPenalty;
      }
    }
  }
  
  // Apply game-type deficit boost (additive, outside weighted dimensions).
  // Allows an under-represented game type to beat a marginally higher-scoring combo
  // of a different type without distorting the dimension weights.
  if (finalScore !== -Infinity) {
    const deficitBoost = computeGameTypeDeficitBoost(team1Objects, team2Objects, sessionState, playersById);
    if (deficitBoost > 0) {
      debugLog('📈 Game-type deficit boost applied', {
        proposedType: getProposedGameType(
          team1Objects,
          team2Objects,
          sessionState?.playerWaitMetrics || {},
          playersById,
        ),
        deficitBoost,
        finalScore,
      });
      finalScore += deficitBoost;
    }
  }

  // Apply player preference penalties (not a weighted dimension, but a direct penalty)
  // This ensures preferences are respected without needing to rebalance other weights
  if (finalScore !== -Infinity && sessionState?.matchPreferencesEnabled !== false) {
    // Import scorePlayerPreferences from scoreEngine
    
    const playerWaitMetrics = sessionState?.playerWaitMetrics || {};
    const preferencePenalty = scorePlayerPreferences(team1, team2, playersById, playerWaitMetrics);
    
    // Scale the penalty to the normalized scoring range (0-100)
    // Original penalty is -8000 per violation, scale it to be significant but not overwhelming
    // -8000 in legacy scale ≈ -40 points in normalized scale (assuming typical scores of 50-80)
    const scaledPenalty = preferencePenalty / 200; // -8000 / 200 = -40
    finalScore += scaledPenalty;
    
    if (preferencePenalty < 0) {
      debugLog('🎯 Normalized preference penalty:', {
        originalPenalty: preferencePenalty,
        scaledPenalty: scaledPenalty.toFixed(2),
        finalScore: finalScore.toFixed(2),
      });
    }
  }

  // Apply cooldown penalty when host disabled hard blocks (soft deterrent only)
  if (finalScore !== -Infinity && cooldownEnabled === false) {
    const cooldownWaitMetrics = sessionState?.playerWaitMetrics || {};
    const cooldownPlayerIds = [...team1, ...team2];
    let onCooldownCount = 0;
    cooldownPlayerIds.forEach((playerId) => {
      const isOnCooldown = cooldownWaitMetrics[playerId]?.isOnCooldown === true
        || (playersPassedCooldown && playersPassedCooldown.size > 0 && !playersPassedCooldown.has(playerId));
      if (isOnCooldown) {
        onCooldownCount++;
      }
    });
    if (onCooldownCount > 0) {
      const legacyPenalty = onCooldownCount * MATCHMAKING_CONFIG.bonuses.cooldownPerPlayer;
      const scaledPenalty = legacyPenalty / 200;
      finalScore -= scaledPenalty;
      debugLog('❄️ Normalized cooldown penalty (soft mode):', {
        onCooldownCount,
        legacyPenalty,
        scaledPenalty: scaledPenalty.toFixed(2),
        finalScore: finalScore.toFixed(2),
      });
    }
  }
  
  return {
    score: finalScore,
    dimensionScores, // Return breakdown for debugging/logging
    blockReason: finalScore === -Infinity ? 'Hard block from dimension scoring' : null,
  };
}

/**
 * Compare old and new scoring systems side-by-side
 * Useful for A/B testing and validation
 * 
 * @param {Function} legacyScoringFn - The original scoreCombination function
 * @param {Object} args - Arguments to pass to both scoring functions
 * @returns {Object} Comparison result with scores and analysis
 */
export function compareScoringSystems(legacyScoringFn, args) {
  const {
    split,
    playersById,
    partnerships,
    sessionState,
    relaxationLevel,
    scheduledCombinations,
    gameTypeRestriction,
    playerCategories,
    gamesMin,
    scheduledGames,
    playersPassedCooldown,
    expandedThreshold,
    scoringConfig,
    hardBlocks,
    cooldownEnabled,
  } = args;
  
  // Run legacy scoring
  const legacyScore = legacyScoringFn(
    split,
    playersById,
    partnerships,
    sessionState,
    relaxationLevel,
    scheduledCombinations,
    gameTypeRestriction,
    playerCategories,
    gamesMin,
    scheduledGames,
    playersPassedCooldown,
    expandedThreshold
  );
  
  // Run normalized scoring
  const normalizedResult = scoreCombinationNormalized(
    split,
    playersById,
    partnerships,
    sessionState,
    relaxationLevel,
    scheduledCombinations,
    gameTypeRestriction,
    playerCategories,
    gamesMin,
    scheduledGames,
    playersPassedCooldown,
    expandedThreshold,
    scoringConfig,
    hardBlocks,
    cooldownEnabled
  );
  
  const { team1, team2 } = split;
  const team1Names = team1.map(id => playersById[id]?.name || id.slice(0, 8)).join(' + ');
  const team2Names = team2.map(id => playersById[id]?.name || id.slice(0, 8)).join(' + ');
  
  // Analyze differences
  const comparison = {
    matchup: `${team1Names} vs ${team2Names}`,
    legacy: {
      score: legacyScore,
      isHardBlock: legacyScore === -Infinity,
    },
    normalized: {
      score: normalizedResult.score,
      isHardBlock: normalizedResult.score === -Infinity,
      blockReason: normalizedResult.blockReason,
      breakdown: normalizedResult.dimensionScores,
    },
    agreement: {
      bothBlock: legacyScore === -Infinity && normalizedResult.score === -Infinity,
      bothAllow: legacyScore !== -Infinity && normalizedResult.score !== -Infinity,
      disagreement: (legacyScore === -Infinity) !== (normalizedResult.score === -Infinity),
    },
  };
  
  return comparison;
}

/**
 * Whether doubles matchmaking uses normalized scoring + relaxation (see evaluateWithRelaxation).
 * Competitive singles uses the normalized singles path by default regardless of this flag;
 * use MATCHMAKING_CONFIG.featureFlags.legacySinglesMatchmaking for pre-normalized singles only.
 */
export function shouldUseNormalizedScoring() {
  return MATCHMAKING_CONFIG.featureFlags?.useNormalizedScoring || false;
}

export function shouldCompareScoringSystems() {
  return MATCHMAKING_CONFIG.featureFlags?.compareScoringSystems || false;
}

export function getScoringConfig() {
  return MATCHMAKING_CONFIG.featureFlags?.scoringConfig || DEFAULT_SCORING_CONFIG;
}
